import { UUID } from "@bitory-gmbh/nidavellir";
import { backendApi } from "@/services/backend/api";
import {
  Extra,
  IncidentalCost,
  Pricing,
} from "@/services/backend/rlt/pricing/types/pricing";

const pricingService = backendApi
  .enhanceEndpoints({
    addTagTypes: ["RltPricing"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      rltPricingShow: build.query<Pricing, ShowRequest>({
        query: (request) => ({
          url: "/rlt/pricing",
          method: "GET",
          params: request,
        }),
        providesTags: (result) =>
          result ? [{ type: "RltPricing", id: result.id }] : [],
      }),
      rltPricingUpdateTreatmentPriceUseCalculation: build.mutation<
        Pricing,
        UpdateTreatmentPriceUseCalculationRequest
      >({
        query: (request) => ({
          url: "/rlt/pricing/treatment-price-use-calculation",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "RltPricing", id: request.id },
          "RltOffer",
          "RltOrder",
          "RltConfirmation",
          "RltWorkOrder",
          "RltPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
      rltPricingUpdateFromCalculation: build.mutation<
        Pricing,
        UpdateFromCalculationRequest
      >({
        query: (request) => ({
          url: "/rlt/pricing/update-from-calculation",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "RltPricing", id: request.id },
          "RltOffer",
          "RltOrder",
          "RltConfirmation",
          "RltWorkOrder",
          "RltPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
      rltPricingUpdateTreatmentPriceBasePrice: build.mutation<
        Pricing,
        UpdateTreatmentPriceBasePriceRequest
      >({
        query: (request) => ({
          url: "/rlt/pricing/treatment-price/base-price",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "RltPricing", id: request.id },
          "RltOffer",
          "RltOrder",
          "RltConfirmation",
          "RltWorkOrder",
          "RltPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
      rltPricingUpdateTreatmentPriceRelativeDiscount: build.mutation<
        Pricing,
        UpdateTreatmentPriceRelativeDiscountRequest
      >({
        query: (request) => ({
          url: "/rlt/pricing/treatment-price/relative-discount",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "RltPricing", id: request.id },
          "RltOffer",
          "RltOrder",
          "RltConfirmation",
          "RltWorkOrder",
          "RltPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
      rltPricingUpdateTreatmentPriceAbsoluteDiscount: build.mutation<
        Pricing,
        UpdateTreatmentPriceAbsoluteDiscountRequest
      >({
        query: (request) => ({
          url: "/rlt/pricing/treatment-price/absolute-discount",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "RltPricing", id: request.id },
          "RltOffer",
          "RltOrder",
          "RltConfirmation",
          "RltWorkOrder",
          "RltPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
      rltPricingUpdateTreatmentPricePostExecutionReduction: build.mutation<
        Pricing,
        UpdateTreatmentPricePostExecutionReductionRequest
      >({
        query: (request) => ({
          url: "/rlt/pricing/treatment-price/post-execution-reduction",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "RltPricing", id: request.id },
          "RltOffer",
          "RltOrder",
          "RltConfirmation",
          "RltWorkOrder",
          "RltPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
      rltPricingUpdateIncidentalCostUseCalculation: build.mutation<
        Pricing,
        UpdateIncidentalCostUseCalculationRequest
      >({
        query: (request) => ({
          url: "/rlt/pricing/incidental-cost-use-calculation",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "RltPricing", id: request.id },
          "RltOffer",
          "RltOrder",
          "RltConfirmation",
          "RltWorkOrder",
          "RltPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
      rltPricingUpdateIncidentalCost: build.mutation<
        Pricing,
        UpdateIncidentalCostRequest
      >({
        query: (request) => ({
          url: "/rlt/pricing/incidental-cost",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "RltPricing", id: request.id },
          "RltOffer",
          "RltOrder",
          "RltConfirmation",
          "RltWorkOrder",
          "RltPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
      rltPricingUpdateExtras: build.mutation<Pricing, UpdateExtrasRequest>({
        query: (request) => ({
          url: "/rlt/pricing/extras",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "RltPricing", id: request.id },
          "RltOffer",
          "RltOrder",
          "RltConfirmation",
          "RltWorkOrder",
          "RltPricingByArea",
          "RelationshipsRelationship",
          "RelationshipsRelationshipCommitment",
          "RelationshipsFulfillment",
        ],
      }),
    }),
  });

export const {
  useRltPricingShowQuery,
  useRltPricingUpdateTreatmentPriceUseCalculationMutation,
  useRltPricingUpdateFromCalculationMutation,
  useRltPricingUpdateTreatmentPriceBasePriceMutation,
  useRltPricingUpdateTreatmentPriceRelativeDiscountMutation,
  useRltPricingUpdateTreatmentPriceAbsoluteDiscountMutation,
  useRltPricingUpdateTreatmentPricePostExecutionReductionMutation,
  useRltPricingUpdateIncidentalCostUseCalculationMutation,
  useRltPricingUpdateIncidentalCostMutation,
  useRltPricingUpdateExtrasMutation,
} = pricingService;

export type ShowRequest = {
  id: UUID;
};

export type UpdateTreatmentPriceUseCalculationRequest = {
  id: UUID;
  value: boolean;
};

export type UpdateFromCalculationRequest = {
  id: UUID;
  calculationId: UUID;
};

export type UpdateTreatmentPriceBasePriceRequest = {
  id: UUID;
  value: number;
};

export type UpdateTreatmentPriceRelativeDiscountRequest = {
  id: UUID;
  active: boolean;
  percentage: number;
};

export type UpdateTreatmentPriceAbsoluteDiscountRequest = {
  id: UUID;
  active: boolean;
  amount: number;
};

export type UpdateTreatmentPricePostExecutionReductionRequest = {
  id: UUID;
  label: string;
  amount: number;
};

export type UpdateIncidentalCostUseCalculationRequest = {
  id: UUID;
  value: boolean;
};

export type UpdateIncidentalCostRequest = {
  id: UUID;
  incidentalCost: IncidentalCost;
};

export type UpdateExtrasRequest = {
  id: UUID;
  extras: Extra[];
};
